import { useEffect, useState } from "react";
import API from "../../services/api";
import Navbar from "../../components/Navbar";

const DUMMY_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Crect width='60' height='60' fill='%23f0f2f5'/%3E%3Ctext x='30' y='35' font-family='Arial' font-size='20' fill='%23999' text-anchor='middle'%3E📦%3C/text%3E%3C/svg%3E";

interface OrderItem {
  order_id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  price: number;
  total_price: number;
  delivery_date: string | null;
  status: string;
  payment_status: string;
  order_type: string;
  type: string;
  image: string | null;
  created_at: string;
  updated_at: string;
}

interface UserOrder {
  delivery_preference: any;
  user_id: string;
  user_name: string;
  email: string;
  phone: string | null;
  address: string | null;
  user_image: string | null;
  items: OrderItem[];
  total_items: number;
  total_amount: number;
  order_count: number;
  order_ids: string[];
  statuses: string[];
  payment_statuses: string[];
  delivery_dates: string[];
  order_types: string[];
}

interface OrdersResponse {
  filter: string;
  date: string | null;
  date_range: string;
  summary: {
    total_users: number;
    total_items: number;
    total_amount: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    has_next?: boolean;
    has_previous?: boolean;
  };
  orders: UserOrder[];
}

export default function Orders() {
  const [ordersData, setOrdersData] = useState<OrdersResponse | null>(null);
  const [filter, setFilter] = useState<string>("tomorrow");
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  const fetchOrders = async (filterType: string, pageNum: number, start?: string, end?: string) => {
    setLoading(true);
    setError(null);
    try {
      let url = `/orders?filter=${filterType}&page=${pageNum}&limit=10`;

      if (filterType === "all" && start && end) {
        url += `&start_date=${start}&end_date=${end}`;
      }

      const res = await API.get(url);
      if (res.data && res.data.success) {
        setOrdersData(res.data.data);
        if (res.data.data.orders.length > 0) {
          const firstUserId = res.data.data.orders[0].user_id;
          setExpandedUsers(new Set([firstUserId]));
        }
      } else {
        setError("Failed to fetch orders");
      }
    } catch (error: any) {
      console.error("Failed to fetch orders", error);
      setError(error?.response?.data?.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter === "all" && showDatePicker) {
      if (startDate && endDate) {
        fetchOrders(filter, page, startDate, endDate);
      }
    } else {
      fetchOrders(filter, page);
    }
  }, [filter, page, startDate, endDate, showDatePicker]);

  const toggleUserExpand = (userId: string) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: "#ff9800",
      confirmed: "#2196f3",
      processing: "#9c27b0",
      delivered: "#4caf50",
      cancelled: "#f44336"
    };
    return statusColors[status?.toLowerCase()] || "#666";
  };

  const getStatusBadgeStyle = (status: string) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
    backgroundColor: getStatusColor(status),
    color: "#fff",
    textTransform: "capitalize" as const,
  });

  const renderPageNumbers = () => {
    if (!ordersData) return null;

    const totalPages = ordersData.pagination.pages;
    const currentPage = ordersData.pagination.page;

    if (totalPages === 0) return null;

    const pageNumbers: number[] = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 5; i++) {
        pageNumbers.push(i);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers.map((pageNum) => (
      <button
        key={pageNum}
        onClick={() => handlePageChange(pageNum)}
        style={{
          ...styles.pageNumberButton,
          ...(pageNum === currentPage ? styles.pageNumberActive : {})
        }}
      >
        {pageNum}
      </button>
    ));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilter = e.target.value;
    setFilter(newFilter);
    setPage(1);

    if (newFilter === "all") {
      setShowDatePicker(true);
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);

      setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
      setEndDate(today.toISOString().split('T')[0]);
    } else {
      setShowDatePicker(false);
      setStartDate("");
      setEndDate("");
    }
  };

  const handlePageChange = (newPage: number) => {
    const totalPages = ordersData?.pagination?.pages || 0;
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleApplyDateRange = () => {
    if (startDate && endDate) {
      setPage(1);
      fetchOrders(filter, 1, startDate, endDate);
    }
  };

  const handleClearDateRange = () => {
    setStartDate("");
    setEndDate("");
    setPage(1);
    fetchOrders(filter, 1);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = DUMMY_IMAGE;
    target.onerror = null;
  };

  const getImageSrc = (imageUrl: string | null) => {
    if (!imageUrl) return DUMMY_IMAGE;
    return imageUrl;
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Order Management</h1>
          <p style={styles.subtitle}>Orders grouped by customer for easy processing</p>
        </div>

        <div style={styles.controls}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Filter Orders:</label>
            <select
              value={filter}
              onChange={handleFilterChange}
              style={styles.select}
            >
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="all">All Orders</option>
            </select>
          </div>

          {showDatePicker && (
            <div style={styles.dateRangeGroup}>
              <div style={styles.dateInputGroup}>
                <label style={styles.dateLabel}>From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
              <div style={styles.dateInputGroup}>
                <label style={styles.dateLabel}>To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
              <button
                onClick={handleApplyDateRange}
                style={styles.applyButton}
                disabled={!startDate || !endDate}
              >
                Apply
              </button>
              <button
                onClick={handleClearDateRange}
                style={styles.clearButton}
              >
                Clear
              </button>
            </div>
          )}

          {ordersData && ordersData.pagination.pages > 0 && (
            <div style={styles.pageInfo}>
              Page {ordersData.pagination.page} of {ordersData.pagination.pages}
            </div>
          )}
        </div>

        {loading && (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Loading orders...</p>
          </div>
        )}

        {error && (
          <div style={styles.error}>
            <p>{error}</p>
            <button onClick={() => fetchOrders(filter, page)} style={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {ordersData && !loading && (
          <>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>👥</div>
                <div>
                  <div style={styles.summaryLabel}>Total Customers</div>
                  <div style={styles.summaryValue}>{ordersData.summary.total_users}</div>
                </div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>🛒</div>
                <div>
                  <div style={styles.summaryLabel}>Total Items</div>
                  <div style={styles.summaryValue}>{ordersData.summary.total_items}</div>
                </div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryIcon}>💰</div>
                <div>
                  <div style={styles.summaryLabel}>Total Amount</div>
                  <div style={styles.summaryValue}>₹{ordersData.summary.total_amount}</div>
                </div>
              </div>
            </div>

            {ordersData.orders.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📭</div>
                <h3>No orders found</h3>
                <p>No orders available for the selected filter</p>
              </div>
            ) : (
              <div style={styles.userOrdersList}>
                {ordersData.orders.map((userOrder: UserOrder) => {
                  const isExpanded = expandedUsers.has(userOrder.user_id);

                  return (
                    <div key={userOrder.user_id} style={styles.userCard}>
                      <div
                        style={styles.userHeader}
                        onClick={() => toggleUserExpand(userOrder.user_id)}
                      >
                        <div style={styles.userInfo}>
                          {userOrder.user_image && (
                            <img
                              src={userOrder.user_image}
                              alt={userOrder.user_name}
                              style={styles.userAvatar}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          <div>
                            <div style={styles.userName}>{userOrder.user_name}</div>
                            <div style={styles.userContact}>
                              <span>{userOrder.email}</span>
                              {userOrder.phone && <span>• {userOrder.phone}</span>}
                            </div>
                            {userOrder.address && (
                              <div style={styles.userAddress}>
                                📍 {userOrder.address}
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={styles.userStats}>
                          <span style={styles.statBadge}>
                            📦 {userOrder.order_count} Order{userOrder.order_count > 1 ? 's' : ''}
                          </span>
                          <span style={styles.statBadge}>
                            🛒 {userOrder.total_items} Items
                          </span>
                          <span style={styles.statBadge} className="amount-badge">
                            ₹{userOrder.total_amount}
                          </span>
                           <span style={styles.statBadge} className="amount-badge">
                            {userOrder.delivery_preference ? userOrder.delivery_preference  : 'NA'}
                          </span>
                          <span style={styles.expandIcon}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={styles.itemsContainer}>
                          <div style={styles.itemsHeader}>
                            <span style={styles.itemsTitle}>Order Items</span>
                            <div style={styles.itemsBadges}>
                              {userOrder.statuses.map(status => (
                                <span key={status} style={getStatusBadgeStyle(status)}>
                                  {status}
                                </span>
                              ))}
                              {userOrder.order_types.includes('subscription') && (
                                <span style={{
                                  ...styles.typeBadge,
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2'
                                }}>
                                  Subscription
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={styles.itemsGrid}>
                            {userOrder.items.map((item, idx) => (
                              <div key={idx} style={styles.itemRow}>
                                <div style={styles.itemImageContainer}>
                                  <img
                                    src={getImageSrc(item.image)}
                                    alt={item.product_name || 'Product'}
                                    style={styles.itemImage}
                                    onError={handleImageError}
                                  />
                                </div>
                                <div style={styles.itemDetails}>
                                  <div style={styles.itemName}>{item.product_name || 'Unknown Product'}</div>
                                  <div style={styles.itemMeta}>
                                    <span>Order: {item.order_id}</span>
                                    <span>•</span>
                                    <span>Qty: {item.quantity}</span>
                                    <span>•</span>
                                    <span>₹{item.price} each</span>
                                    {item.delivery_date && (
                                      <>
                                        <span>•</span>
                                        <span style={styles.deliveryDate}>📅 {item.delivery_date}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div style={styles.itemTotal}>
                                  <span style={styles.itemTotalLabel}>Total</span>
                                  <span style={styles.itemTotalAmount}>₹{item.total_price}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {ordersData &&
              ordersData.orders.length > 0 &&
              ordersData.pagination.pages > 1 && (
                <div style={styles.pagination}>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    style={{
                      ...styles.pageButton,
                      ...(page === 1 ? styles.pageButtonDisabled : {})
                    }}
                  >
                    ← Previous
                  </button>

                  <div style={styles.pageNumbers}>
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === ordersData.pagination.pages}
                    style={{
                      ...styles.pageButton,
                      ...(page === ordersData.pagination.pages ? styles.pageButtonDisabled : {})
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
          </>
        )}
      </div>
    </>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a2332",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  controls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "#fff",
    padding: "8px 16px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#555",
  },
  select: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    backgroundColor: "#fff",
    cursor: "pointer",
    outline: "none",
    minWidth: "150px",
  },
  dateRangeGroup: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "#fff",
    padding: "8px 16px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    flexWrap: "wrap",
  },
  dateInputGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  dateLabel: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
  },
  dateInput: {
    padding: "6px 10px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    fontSize: "13px",
    outline: "none",
    minWidth: "140px",
  },
  applyButton: {
    padding: "6px 16px",
    backgroundColor: "#2196f3",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
  clearButton: {
    padding: "6px 16px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
  pageInfo: {
    fontSize: "14px",
    color: "#666",
    backgroundColor: "#fff",
    padding: "8px 16px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  loading: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    color: "#666",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },
  error: {
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "8px",
    padding: "16px 20px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#d32f2f",
  },
  retryButton: {
    padding: "8px 20px",
    backgroundColor: "#f44336",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  summaryCard: {
    backgroundColor: "#fff",
    padding: "16px 20px",
    borderRadius: "10px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px solid #e8ecf1",
  },
  summaryIcon: {
    fontSize: "28px",
  },
  summaryLabel: {
    fontSize: "12px",
    color: "#888",
    fontWeight: "500",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  summaryValue: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1a2332",
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "60px 20px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    border: "1px dashed #ddd",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  userOrdersList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  userCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  userHeader: {
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: "#fafbfc",
    borderBottom: "1px solid #e8ecf1",
    transition: "background-color 0.2s",
    flexWrap: "wrap",
    gap: "12px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  userAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover" as const,
    backgroundColor: "#e8ecf1",
  },
  userName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a2332",
  },
  userContact: {
    fontSize: "13px",
    color: "#666",
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  userStats: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  statBadge: {
    padding: "4px 12px",
    backgroundColor: "#f0f2f5",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#333",
  },
  expandIcon: {
    fontSize: "18px",
    color: "#666",
    marginLeft: "4px",
  },
  itemsContainer: {
    padding: "16px 20px",
    backgroundColor: "#fafbfc",
  },
  itemsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    flexWrap: "wrap",
    gap: "8px",
  },
  itemsTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#555",
  },
  itemsBadges: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },
  itemsGrid: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #e8ecf1",
    transition: "transform 0.2s",
    flexWrap: "wrap",
  },
  itemImageContainer: {
    width: "60px",
    height: "60px",
    flexShrink: 0,
  },
  itemImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    borderRadius: "6px",
    backgroundColor: "#f5f5f5",
  },
  itemDetails: {
    flex: 1,
    minWidth: "150px",
  },
  itemName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#1a2332",
    marginBottom: "4px",
  },
  itemMeta: {
    fontSize: "12px",
    color: "#888",
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  deliveryDate: {
    color: "#2196f3",
    fontWeight: "500",
  },
  itemTotal: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-end",
    minWidth: "80px",
  },
  itemTotalLabel: {
    fontSize: "11px",
    color: "#888",
    textTransform: "uppercase" as const,
  },
  itemTotalAmount: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#2196f3",
  },
  typeBadge: {
    padding: "2px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
    textTransform: "capitalize" as const,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    marginTop: "24px",
    padding: "16px 0",
    flexWrap: "wrap",
  },
  pageButton: {
    padding: "8px 16px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    transition: "all 0.2s",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  pageNumbers: {
    display: "flex",
    gap: "4px",
  },
  pageNumberButton: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    transition: "all 0.2s",
  },
  pageNumberActive: {
    backgroundColor: "#2196f3",
    borderColor: "#2196f3",
    color: "#fff",
  },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);